<template>
	<div class="bb-panel-wrapper">
		<div
			class="bb-input-wrapper email-subject-container"
			v-show="selectedMediaType === MEDIA_ID.EMAIL"
		>
			<span class="b360-form-label" role="heading"
				>Email Subject Line</span
			>
			<BaseFormInput
				name="email-subject"
				:icon="['far', 'undo']"
				iconLabel="revert to default email subject"
				:value="subjectLine"
				@input="handleEmailSubjectInput"
				@onIconClick="handleResetEmailSubject"
				:error="emailSubjectEmpty"
				@onFocus="handleEmailSubjectFocus"
				@onBlur="handleEmailSubjectBlur"
			/>
			<span
				class="error"
				v-if="emailSubjectEmpty"
				data-test="email-subject-error"
				>* required</span
			>
		</div>
		<div class="text-option-container" v-if="showVariations">
			<span class="b360-form-label" role="heading">Select Variation</span>
			<select
				name="text-option-selector"
				@change="changeVariation"
				class="b360-form-input text-option-selector"
			>
				<option
					v-for="(option, index) in selectVariations"
					:key="index"
					:value="index"
					:selected="selectedTextVariationIndex === index"
				>
					{{ option }}
				</option>
			</select>
		</div>
		<div
			class="bb-input-wrapper"
			v-for="control in updatedSelectedTextControls"
			:key="control.id"
			v-show="
				!(
					selectedMediaType === MEDIA_ID.PRICING_FLYER &&
					control.clientId === 'endingTagLineText'
				)
			"
		>
			<RichText
				:control="control"
				:variationIndex="selectedTextVariationIndex"
				:labelName="control.label"
				:fieldName="control.clientId"
				:ref="control.clientId"
				@focus="richTextControlFocused"
				@blur="richTextControlBlurred"
			/>
		</div>
		<button
			class="b360-btn b360-btn--workspace-primary"
			@click="nextStep()"
		>
			Next Step
		</button>
		<Modal
			modalTitle="Legal Notice"
			actionBtnName="Agree and Continue"
			class="md legal-modal disable-padding"
			v-if="showModal"
			:show="showModal"
			@close="agreeModalClose()"
			@clicked="agreeModalAccept()"
		>
			<div class="text-modal-copy" slot="modalContent">
				<p>
					You are altering legally approved content. Please accept the
					<a @click="showTermsOfUse = !showTermsOfUse"
						>Terms of Use</a
					>
					by clicking <strong>Agree and Continue</strong>.
				</p>
				<p class="disclaimer-wrapper" v-show="showTermsOfUse">
					<TermsOfUseSlotComponent />
				</p>
			</div>
		</Modal>
	</div>
</template>

<script>
/* eslint-disable no-param-reassign */
import { mapState, mapActions, mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';

import BaseFormInput from '@/components/global/BaseFormInput.vue';
import Modal from '@/components/global/Modal.vue';
import RichText from '@/components/brand-builder/RichText.vue';
import TermsOfUseSlotComponent from '@/components/legal/TermsOfUseSlotComponent.vue';
import { MEDIA_ID } from '@/constants/brand-builder';

export default {
	name: 'BbText',
	components: {
		BaseFormInput,
		Modal,
		RichText,
		TermsOfUseSlotComponent,
	},
	data() {
		return {
			showTermsOfUse: false,
			showModal: false,
			agreedToDisclaimer: false,
			updatedSelectedTextControls: null,
			focusedEmailSubject: null,
			focusedRichTextControlId: null,
			MEDIA_ID,
			emailSubjectEmpty: false,
		};
	},
	computed: {
		...mapState({
			selectVariations: (state) => {
				const noTagsRegex = /(<([^>]+)>)/gi;
				const variationDisplay = Object.freeze(
					state.brandBuilderEditor.selectedItem.variations.map(
						(variation) =>
							variation.name &&
							variation.name.replace(noTagsRegex, ''),
					),
				);
				return variationDisplay;
			},
			selectedTextVariationIndex: (state) =>
				state.brandBuilderEditor.selectedTextVariationIndex,
			selectedMediaType: (state) =>
				state.brandBuilderEditor.selectedMediaType,
			selectedTextControls: (state) => {
				const selectedVariation =
					state.brandBuilderEditor.selectedItem.variations[
						state.brandBuilderEditor.selectedTextVariationIndex
					];
				return selectedVariation.controls.find(
					(control) => control.category === 'Text',
				).items;
			},
			borrowerName: (state) =>
				state.brandBuilderEditor.clientConnectAlertEmail
					.borrowerFirstName,
			socialMediaLinks: (state) =>
				state.companyProfile.socialMediaDetails,
			defaultSubjectLine: (state) =>
				state.brandBuilderEditor.defaultSubjectLine,
			subjectLine: (state) => state.brandBuilderEditor.subjectLine,
		}),
		...mapGetters(['showVariations']),
		showVariations() {
			if (this.selectVariations.length > 1) return true;
			return false;
		},
	},
	created() {
		this.updatedSelectedTextControls = cloneDeep(this.selectedTextControls);
		this.updateBorrowerName();
		this.updateGoogleMyBusinessLink();
		this.updatedSelectedTextControls.forEach((control) => {
			this.updateControlValue({
				value: control.value,
				fieldName: control.clientId,
			});
		});
		this.setRouteAllowed({ route: 'design', activated: true });
		if (this.subjectLine.length === 0) {
			this.setEmailSubjectLine(this.defaultSubjectLine);
		}
	},
	destroyed() {
		if (this.emailSubjectEmpty === true) {
			this.handleResetEmailSubject();
		}
	},
	methods: {
		...mapActions([
			'updateControlValue',
			'selectCopyVariation',
			'updateSelectedControlValue',
			'setRouteAllowed',
			'setEmailSubjectLine',
		]),
		handleEmailSubjectInput(value) {
			this.setEmailSubjectLine(value);
		},
		handleResetEmailSubject() {
			this.setEmailSubjectLine(this.defaultSubjectLine);
		},
		handleEmailSubjectFocus(e) {
			if (this.agreedToDisclaimer === false) {
				this.focusedEmailSubject = e;
				this.agreeModal();
			}
		},
		handleEmailSubjectBlur() {
			if (this.showModal === false) {
				this.focusedEmailSubject = null;
			}
		},
		updateBorrowerName() {
			if (this.borrowerName) {
				this.updatedSelectedTextControls =
					this.updatedSelectedTextControls.map((textControl) => {
						if (
							textControl.clientId === 'bodyText' &&
							this.borrowerName
						) {
							textControl.value = textControl.value.replace(
								'[Borrowername]',
								this.borrowerName,
							);
							textControl.value = textControl.value.replace(
								'[Borrower Name]',
								this.borrowerName,
							);
						}
						return textControl;
					});
			}
		},
		updateGoogleMyBusinessLink() {
			const googleMyBusinessLink = this.socialMediaLinks
				.filter((link) => link.type === 'GoogleMyBusiness')
				.map((link) => link.value);
			if (googleMyBusinessLink.length > 0) {
				this.updatedSelectedTextControls =
					this.updatedSelectedTextControls.map((textControl) => {
						if (textControl.clientId === 'bodyText') {
							textControl.value = textControl.value.replace(
								'[GMB LINK]',
								`https://www.${googleMyBusinessLink}`,
							);
						}
						return textControl;
					});
			}
		},
		richTextControlFocused(e) {
			this.focusedRichTextControlId = e.control.clientId;
			if (this.agreedToDisclaimer === false) {
				this.agreeModal();
			}
		},
		richTextControlBlurred(e) {
			if (
				this.showModal === false &&
				this.focusedRichTextControlId === e.control.clientId
			) {
				this.focusedRichTextControlId = null;
			}
		},
		agreeModal() {
			this.showModal = true;
			document.activeElement.blur();
		},
		agreeModalAccept() {
			this.showModal = false;
			this.agreedToDisclaimer = true;
			if (this.focusedRichTextControlId === true) {
				this.$refs[this.focusedRichTextControlId][0].editor.focus();
			}
			if (this.focusedEmailSubject) {
				this.focusedEmailSubject.focus();
				this.focusedEmailSubject = null;
			}
		},
		agreeModalClose() {
			/* Closed without agreeing */
			this.showModal = false;
			this.focusedRichTextControlId = null;
			this.focusedEmailSubject = null;
		},
		changeVariation(e) {
			this.selectCopyVariation(e);
			this.updatedSelectedTextControls = cloneDeep(
				this.selectedTextControls,
			);
			this.updateBorrowerName();
			this.updateGoogleMyBusinessLink();
			this.updatedSelectedTextControls.forEach((control) => {
				this.updateControlValue({
					value: control.value,
					fieldName: control.clientId,
				});
			});
		},
		updateTextControl(data) {
			/* NOTE: Don't remove updateTextControl()!  It is being called directly from FormRichText.vue child component */
			this.updateControlValue(data);
			this.updateSelectedControlValue(data);
		},
		nextStep() {
			if (
				this.selectedMediaType === MEDIA_ID.FLYER ||
				this.selectedMediaType === MEDIA_ID.EMAIL ||
				this.selectedMediaType === MEDIA_ID.SOCIAL_MEDIA
			) {
				this.$router.push({
					path: `/campaign-editor-workspace/${this.$route.params.campaignId}/design`,
				});
			} else if (this.selectedMediaType === MEDIA_ID.PRICING_FLYER) {
				this.$router.push({
					path: `/campaign-editor-workspace/${this.$route.params.campaignId}/branding`,
				});
			}
		},
	},
	watch: {
		subjectLine(value) {
			if (value.length === 0) {
				this.emailSubjectEmpty = true;
			} else {
				this.emailSubjectEmpty = false;
			}
		},
	},
};
</script>

<style scoped lang="scss">
.text-modal-copy {
	@include rem(padding, 0 12px 12px 12px);
	@include rem(margin-bottom, 0);
}

.disclaimer-wrapper {
	width: 100%;
	@include rem(height, 300px);
	list-style-type: none;
	padding: 0;
	margin: 0;
	border: $b360-border-width-thin solid $border-color-light;
	border-radius: $b360-border-radius-base;
	/* scrolling */
	scroll-snap-type: y proximity;
	overflow: hidden;
	overflow-y: scroll;

	&::-webkit-scrollbar {
		@include rem(width, 2px);
		background: none;
	}

	&::-webkit-scrollbar-thumb {
		background: $brand-blue;
	}

	.license-item {
		@include rem(padding, 0 10px);
		@include rem(font-size, 20px);
		@include flex-center;
		justify-content: space-between;
		scroll-snap-align: start;
		@include rem(margin-right, 2px);

		&:hover,
		&.active {
			color: $brand-blue;
			background: $b360-color--blue__lightest;
		}
	}
}

p a {
	cursor: pointer;
}

.text-option-container {
	@include rem(padding-bottom, 21px);

	.text-option-selector {
		@include rem(padding-top, 10px);
	}
}

.email-subject-container {
	@include rem(padding-bottom, 21px);
}

.error {
	color: $error;
	font-style: italic;
}
</style>
